import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { FaUsers, FaGavel, FaClipboardCheck, FaMedkit, FaShieldAlt, FaChartLine, FaHandshake, FaNewspaper } from 'react-icons/fa';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { contentAPI } from '../services/api';

const iconMap = {
  'FaGavel': FaGavel,
  'FaUsers': FaUsers,
  'FaClipboardCheck': FaClipboardCheck,
  'FaChartLine': FaChartLine,
  'FaShieldAlt': FaShieldAlt,
  'FaNewspaper': FaNewspaper,
  'FaMedkit': FaMedkit,
  'FaHandshake': FaHandshake,
  '🏆': FaGavel,
  '⚖️': FaShieldAlt,
  '🏴': FaGavel,
  '📅': FaClipboardCheck,
  '⚽': FaChartLine
};

const CommitteesPage = () => {
  const { language } = useLanguage();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await contentAPI.getCommittees();
        const data = res.data || res || [];
        setCommittees(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const text = {
    ar: {
      title: 'اللجان',
      subtitle: 'اللجان الإدارية والفنية للرابطة الوطنية لكرة القدم للهواة',
      totalCommittees: 'لجنة',
      members: 'الأعضاء',
      contact: 'للاتصال',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      contactInfo: 'معلومات الاتصال'
    },
    fr: {
      title: 'Les Comités',
      subtitle: 'Comités administratifs et techniques de la Ligue Nationale de Football Amateur',
      totalCommittees: 'Comités',
      members: 'Membres',
      contact: 'Contact',
      email: 'Email',
      phone: 'Téléphone',
      contactInfo: 'Informations de contact'
    }
  };

  const t = text[language];

  if (loading) {
    return <div style={{ padding: '100px', textAlign: 'center' }}>Chargement...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <h1 style={styles.title}>{t.title}</h1>
        <p style={styles.subtitle}>{t.subtitle}</p>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{committees.length}</span>
            <span style={styles.statLabel}>{t.totalCommittees}</span>
          </div>
        </div>
      </section>

      {/* Committees Grid */}
      <section style={styles.committeesSection}>
        <div style={styles.committeesGrid}>
          {committees.map((committee, index) => {
            const Icon = iconMap[committee.icon];
            const isEmoji = !Icon && committee.icon?.length <= 2;

            return (
              <div
                key={committee.id}
                style={{
                  ...styles.committeeCard,
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div style={{ ...styles.committeeHeader, background: committee.color || '#c41e3a' }}>
                  {Icon ? (
                    <Icon style={styles.committeeIcon} />
                  ) : isEmoji ? (
                    <span style={{ fontSize: '60px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>{committee.icon}</span>
                  ) : (
                    <FaUsers style={styles.committeeIcon} />
                  )}
                </div>

                <div style={styles.committeeBody}>
                  <h3 style={styles.committeeName}>
                    {language === 'ar' ? committee.name_ar : committee.name}
                  </h3>

                  <p style={styles.committeeDescription}>
                    {language === 'ar' ? committee.description_ar : committee.description}
                  </p>

                  <div style={styles.membersSection}>
                    <h4 style={styles.membersTitle}>
                      <FaUsers style={styles.membersIcon} />
                      {t.members}
                    </h4>
                    <div style={styles.membersList}>
                      {(committee.members || []).map((member, idx) => (
                        <div key={idx} style={styles.memberItem}>
                          <div style={styles.memberAvatar}>
                            {member.image_url ? (
                              <img
                                src={member.image_url}
                                alt={member.name}
                                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerText = (language === 'ar' ? member.name_ar : member.name).charAt(0); }}
                              />
                            ) : (
                              (language === 'ar' ? member.name_ar : member.name).charAt(0)
                            )}
                          </div>
                          <div style={styles.memberInfo}>
                            <div style={styles.memberName}>{language === 'ar' ? member.name_ar : member.name}</div>
                            <div style={styles.memberRole}>{language === 'ar' ? member.role_ar : member.role}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '80px 20px 60px',
    textAlign: 'center',
    color: 'white'
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    fontSize: '20px',
    marginBottom: '30px',
    opacity: 0.9,
    maxWidth: '800px',
    margin: '0 auto 30px'
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.2)',
    padding: '20px 40px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px'
  },
  committeesSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '60px 20px'
  },
  committeesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '30px'
  },
  committeeCard: {
    background: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
    animation: 'fadeInUp 0.6s ease forwards',
    opacity: 0
  },
  committeeHeader: {
    padding: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px'
  },
  committeeIcon: {
    fontSize: '60px',
    color: 'white',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
  },
  committeeBody: {
    padding: '30px'
  },
  committeeName: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '15px',
    textAlign: 'center'
  },
  committeeDescription: {
    fontSize: '15px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '25px',
    textAlign: 'center',
    minHeight: '60px'
  },
  membersSection: {
    borderTop: '2px solid #f0f0f0',
    paddingTop: '20px'
  },
  membersTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  membersIcon: {
    color: '#c41e3a',
    fontSize: '16px'
  },
  membersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '10px',
    transition: 'all 0.3s'
  },
  memberAvatar: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  memberInfo: {
    flex: 1
  },
  memberName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '3px'
  },
  memberRole: {
    fontSize: '13px',
    color: '#666'
  }
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
  @media (max-width: 768px) { .committeeCard { margin: 0 10px; } }
`;
document.head.appendChild(styleSheet);

export default CommitteesPage;
